import { ConfigContext } from '@/contextAPI/configContext';
import { selectAllDashboardData, selectCurrentCycle, selectDashboardIsLoading } from '@/redux/selectors/dashboardSelectors';
import { DashboardData, JobTypeData, TotalJobsCompletedByType } from '@/redux/slices/slice_types';
import { useContext, useEffect, useState } from 'react';
import DashboardWidget, { WidgetData } from '../DashboardWidget';
import { LineChartData } from '../Reusable/LineChart';
import { generateColorArrayFromConfigs, generatePrettyDates, generateRandomNumbers } from '../Reusable/chartUtils';
import { PreBuiltGraphs } from '../../DefaultGraphs';
import { colorConsoleLog } from '../../../../_utils/utils';
import { useAppSelector } from '@/redux/hooks';
import { parse, isAfter, isEqual } from 'date-fns';

const defaultJobTypes = ['edit', 'delivery', 'tech_review'];

type ParsedJobsData = {
  label: string;
  data: JobTypeData['count'][];
};
function parseCompletedJobsData(monthsData: TotalJobsCompletedByType, jobTypes: string[]): ParsedJobsData[] {
  // generate the line objects to be used in the chart
  const lines: {
    id?: string;
    label: string;
    data: JobTypeData['count'][];
  }[] = jobTypes.map((jobType) => ({
    id: jobType,
    label: '',
    data: [] as JobTypeData['count'][]
  }));

  // map the line indices to the job types
  const lineIndices = lines.map((line) => line.id);

  // populate the lines with data
  for (const month in monthsData) {
    for (const jobType of monthsData[month]) {
      if (jobTypes.includes(jobType.job_type)) {
        const lineIndex = lineIndices.indexOf(jobType.job_type);
        lines[lineIndex].label = jobType.job_type_display;
        lines[lineIndex].data.push(jobType.count);
      }
    }
  }

  // remove ids from lines
  lines.forEach((line) => {
    if (line.id) delete line.id;
  });

  return lines;
}

function getJobTypes(jobTypes: string[]): string[] {
  return jobTypes.length <= 0 ? defaultJobTypes : jobTypes;
}

const calculateTotals = (data: DashboardData['totalJobsCompletedByType']): number[] => {
  const totals: number[] = [];

  Object.keys(data).forEach((date) => {
    let total = 0;
    data[date].forEach((jobType) => {
      total += jobType.count;
    });
    totals.push(total);
  });

  return totals;
};

const generateDefaultChart = (jobTypes: string[], colors: any): LineChartData => {
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    months.push(`${month} ${year}`);
  }

  const lines = [...jobTypes, 'total'].map((jobType) => ({
    label: jobType.length > 2 ? jobType.toLowerCase().replace('_', ' ') : jobType,
    data: generateRandomNumbers(6, 25)
  }));

  return {
    id: PreBuiltGraphs.COMPLETED_JOBS,
    noDataMessage: 'No Data Available',
    labels: months,
    values: lines,
    colors: colors,
    legend: 'square',
    axes: {
      x: 'Months',
      y: '# of Jobs'
    }
  };
};

const CompletedJobs = ({ title, caption }: WidgetData) => {
  const currentCycle = useAppSelector(selectCurrentCycle);
  const isLoading = useAppSelector(selectDashboardIsLoading);
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const completedJobs = dashboardConfig?.graphSettings?.['completed-jobs'];
  const dashboardData = useAppSelector(selectAllDashboardData);
  const jobTypes = getJobTypes(
    completedJobs
      ? completedJobs.functional_displayed_job_types !== undefined
        ? completedJobs.functional_displayed_job_types(dashboardData.totalJobsCompletedByType)
        : completedJobs.displayed_job_types
      : []
  );
  const colors = generateColorArrayFromConfigs(
    6, // hardcoded number of data points returned from generateDefaultChart
    dashboardConfig.chartColors,
    dashboardConfig.graphSettings['completed-jobs'] ? dashboardConfig.graphSettings['completed-jobs'].colors : []
  );

  const [lineChartData, setLineChartData] = useState<LineChartData>(generateDefaultChart(jobTypes, colors));

  const buildLineChartData = (data: DashboardData['totalJobsCompletedByType']) => {
    // only show data after the earliest date
    const datesAfterEarliestDate = dashboardConfig?.earliestDate
      ? Object.keys(data).filter((date) => {
          const parsedCurrent = new Date(date);
          const parsedEarliest = parse(dashboardConfig.earliestDate, 'M-d-yyyy', new Date());
          return isAfter(parsedCurrent, parsedEarliest) || isEqual(parsedCurrent, parsedEarliest);
        })
      : Object.keys(data);
    const dataAfterEarliestDate: DashboardData['totalJobsCompletedByType'] = datesAfterEarliestDate.reduce(
      (acc, curr) => ({ ...acc, [curr]: data[curr] }),
      {}
    );
    // generate new array with appropriate number of colors
    const newColors = generateColorArrayFromConfigs(
      datesAfterEarliestDate.length,
      dashboardConfig.chartColors,
      dashboardConfig.graphSettings['completed-jobs'] ? dashboardConfig.graphSettings['completed-jobs'].colors : []
    );

    const labels = generatePrettyDates(datesAfterEarliestDate);
    const values = parseCompletedJobsData(dataAfterEarliestDate, jobTypes);

    // calculate totals and add to chart data
    values.push({
      label: 'Total',
      data: calculateTotals(dataAfterEarliestDate)
    });

    // if there's only one month, add blank label and zero so line shows
    if (values[0].data.length === 1) {
      labels.unshift('');
      values.map((value) => value.data.unshift(0));
    }

    return {
      labels: labels,
      values: values,
      colors: newColors
    };
  };

  useEffect(() => {
    if (dashboardData.totalJobsCompletedByType && !isLoading) {
      const dateKeys = Object.keys(dashboardData.totalJobsCompletedByType).map((date) => ({
        key: date,
        value: date.slice(0, -1) + 1
      }));

      let newData = {} as DashboardData;
      let noDataMessage = null;

      // filter months according to the date and build backup (default) data
      for (let i = 0; i < dateKeys.length && Object.keys(newData).length <= 6; i++) {
        if (typeof currentCycle === 'object' || currentCycle.toLowerCase() === 'all' || (currentCycle.toLowerCase() !== 'all' && dateKeys[i].key <= currentCycle)) {
          newData = {
            ...newData,
            [dateKeys[i].key]: dashboardData.totalJobsCompletedByType[dateKeys[i].key]
          };
        }
      }

      // if no data, set flag and use default data
      if (Object.keys(newData).length < 1) {
        noDataMessage = typeof currentCycle === 'string' && currentCycle.toLowerCase() === 'all' ? 'No Data Available' : 'No Cycle Data Available';
      }

      const builtCycleData = buildLineChartData(newData);
      const totalCount = builtCycleData.values.reduce((acc, curr) => acc + curr.data.reduce((acc, curr) => acc + curr, 0), 0);

      if (Object.keys(newData).length > 0 && totalCount > 0) {
        setLineChartData({
          ...lineChartData,
          noDataMessage: noDataMessage,
          ...builtCycleData
        });
      } else {
        if (!noDataMessage && (totalCount === 0 || isNaN(totalCount))) {
          colorConsoleLog('Completed Jobs Widget showing no data because cumulative total of all visible job types is 0.', 'teal');
          noDataMessage = 'No Data Available';
        }
        setLineChartData({
          ...lineChartData,
          noDataMessage: noDataMessage
        });
      }
    }
  }, [currentCycle]);

  return <DashboardWidget title={title} caption={caption} data={lineChartData} type="line" />;
};

export default CompletedJobs;
